<template>
  <div class="h-100" :style="showSidePanel ? 'margin-right: 355px;' : ''">
    <div v-if="!loadingState && !loadingActiveSubscription && workflow && workflow.id === $route.params.id"
         class="d-flex flex-column h-100"
         :data-test-workflow="true"
         :data-test-version="latestVersionNumberForCypress"
    >
      <navigation-top>
        <navigation-top-workflow
          :previousVersionExists="previousVersionExists"
          :versions="versions"
          :releases="releases"
          :showDone="showDone"
        />
      </navigation-top>

      <bpmn
        ref="bpmn"
        :key="JSON.stringify(showReleaseNo)"
        :workflow="workflow"
        :show-done="showDone"
        @move-right="moveRight"
        @move-left="moveLeft"
      ></bpmn>

      <div class="bottom-container">
        <div class="tab-button-container">
          <v-tabs
            :modelValue="selectedTab"
            @update:model-value="openTab($event)"
            class="new-tab large"
          >
            <v-tab>Backlog</v-tab>
            <v-tab>User Story Map</v-tab>
            <!-- <v-tab id="data-model-tab">Read Model & Command</v-tab> -->
            <v-tab>Domain Model</v-tab>
            <v-tab>Entities</v-tab>
            <v-tab>History</v-tab>
          </v-tabs>
        </div>

        <workflow-backlog
          v-if="Number(selectedTab) === 0 || Number(selectedTab) === 1"
          :eventId="null"
          :workflow="workflow"
          :show-done="showDone"
          :jiraData2="jiraData2"
          :loadingJiraInfo="loadingJiraInfo"
          :selectedTab="selectedTab"
          :releases="releases"
          @show-all="showAll"
          @display-done="onShowDoneChange"
          @ask-for-reply="onAskForReply"
          ref="backlog"
        ></workflow-backlog>

        <domain-model v-if="Number(selectedTab) === 2 && svgGraphData && svgLanesData" />

        <entities v-if="Number(selectedTab) === 3" />

        <workflow-history
          :workflow="workflow"
          :versions="versions"
          v-if="Number(selectedTab) === 4"
        ></workflow-history>

        <workflow-delete-item-dialog
          ref="deleteItemDialog"
          :itemType="'Event'"
          :hideIcon="true"
        ></workflow-delete-item-dialog>
      </div>
    </div>
    <not-found v-if='!globalLoadingState && !loadingState && !workflow'/>
  </div>
</template>

<script>
import Bpmn from '@/components/Bpmn'
import WorkflowBacklog from '@/components/WorkflowBacklog'
import Entities from '@/components/Entities.vue'
import WorkflowHistory from '@/components/WorkflowHistory'
import WorkflowDeleteItemDialog from '@/components/WorkflowDeleteItemDialog'
import workflowApi from '@/api/workflowApi'
import recentWorkflowsApi from '@/api/recentWorkflowsApi'
import { diagramHelpers } from '@/mixins/diagramHelpers.js'
import { workflowHelpers } from '@/mixins/workflowHelpers.js'
import { scrollToEvent } from '@/utils/scroll'
import dataHelpers from '@/helpers/dataHelpers'
import inputHelpers from '@/helpers/inputHelpers'
import cognitoAuth from '@/cognito'
import { EventBus } from '@/event-bus/event-bus'
import NavigationTop from '@/components/NavigationTop'
import DomainModel from '@/components/DomainModel.vue'
import NavigationTopWorkflow from '@/components/NavigationTopWorkflow'
import NotFound from '@/pages/NotFound'
import { mapActions, mapGetters, mapState } from 'vuex'
import 'svg2pdf.js'
import copyPasteHelpers from '../helpers/copyPasteHelpers'
import store from '@/store'

export default {
  name: 'Workflow',
  components: {
    Entities,
    Bpmn,
    WorkflowBacklog,
    WorkflowHistory,
    NavigationTop,
    NotFound,
    WorkflowDeleteItemDialog,
    NavigationTopWorkflow,
    DomainModel
  },
  mixins: [diagramHelpers, workflowHelpers],
  data () {
    return {
      eventType: '',
      showDone: false,
      latestVersionNumberForCypress: null,
      accessToken: null,
      loadingJiraInfo: 0,
      checkVersionTimeout: null
    }
  },
  computed: {
    ...mapState({
      workflow: state => state.workflow.workflow,
      workflowHistory: state => state.workflowHistory.workflowHistory,
      jiraIssues: state => state.jira.jiraIssues,
      showReleaseNo: state => state.workflow.showReleaseNo,
      requirementTypes: state => state.requirementTypes.requirementTypes,
      showSidePanel: state => state.showSidePanel,
      projectMemberships: state => state.projectMemberships.projectMemberships,
      isUserLoggedIn: state => state.isUserLoggedIn
    }),
    ...mapGetters({
      loadingState: 'workflow/getLoadingState',
      globalLoadingState: 'loading/getLoadingState',
      loadingActiveSubscription: 'loading/getLoadingActiveSubscription',
      svgGraphData: 'svgGraphData/svgGraphData',
      svgLanesData: 'svgGraphData/svgLanesData',
      svgGroupsData: 'svgGraphData/svgGroupsData'
    }),
    versions () {
      const res = this.workflowHistory ? [...this.workflowHistory] : []
      return res
    },
    selectedTab () {
      if (this.$route.query?.selectedTab) {
        return this.$route.query.selectedTab
      } else {
        return 0
      }
    },
    previousVersionExists () {
      let index
      if (this.workflow) {
        const restoreVersion = this.workflow.sameAsVersion ? (this.workflow.sameAsVersion - 1) : this.workflow.version - 1
        index = this.versions?.findIndex(element => element.version === restoreVersion)
      }
      return index >= 0
    },
    events () {
      return this.workflow.eventsJson || []
    },
    releases () {
      if (!this.events) {
        return []
      }
      let requirements = []
      for (const event of this.events) {
        if (event.requirementsJson) {
          requirements = requirements.concat(event.requirementsJson)
        }
      }
      const res = new Set()
      for (const requirement of requirements) {
        if (requirement.releaseNo) {
          res.add(requirement.releaseNo)
        }
      }
      return [...res]
        .sort(function (a, b) {
          return a - b
        })
        .map(value => ({
          value,
          text: `R${value}`,
          title: `R${value}`
        }))
    },
    selectedEvent () {
      const { eventId } = this.$route.params
      if (eventId && this.events) {
        const event = this.events.find(wt => wt.id === eventId)
        return event || null
      }
      return null
    },
    jiraData2 () {
      const result = this.jiraIssues?.reduce(function (map, req) {
        map[req.requirementId] = req
        return map
      }, {})
      return result || {}
    }
  },
  watch: {
    projectMemberships: {
      handler: function (memberships) {
        if (!this.isUserLoggedIn || memberships) {
          this.updateWorkflowQueries(this.$route.params.id)
        }
      },
      immediate: true
    },
    'workflow.version': {
      handler: function (newVal) {
        this.latestVersionNumberForCypress = newVal
      },
      immediate: true
    },
    'workflow.status': {
      handler: function (newVal) {
        this.updateWorkflowStatus(newVal)
      },
      immediate: true
    },
    'workflow.id': {
      handler: async function (newVal) {
        if (!this.$route.params.projectId || !this.$route.params.id || !newVal) {
          return
        }

        try {
          const session = await cognitoAuth.getSession()
          this.accessToken = session.getAccessToken().getJwtToken()
          if (this.accessToken) {
            this.$store.dispatch('jira/getJiraIssues', {
              apiToken: this.accessToken,
              projectId: this.$route.params.projectId,
              workflowId: this.$route.params.id
            })
          }
        } catch (error) {
        }
        recentWorkflowsApi.add(this.workflow)
      },
      immediate: true
    },
    '$store.state.workflow.isViewOnlyEnabled': {
      handler: function (newVal) {
        this.updateWorkflowStatus(this.workflow?.status)
      }
    }
  },
  created () {
    window.addEventListener('keydown', this.keydown)
    document.addEventListener('copy', this.onCopy)
    document.addEventListener('paste', (event) => {
      this.debounceUpdateWorkflow({ fn: this.onPaste, args: [event] })
    })
    document.addEventListener('cut', (event) => {
      this.debounceUpdateWorkflow({ fn: this.onCut, args: [event] })
    })
  },
  beforeUnmount () {
    // it is imortant to remove event listener when component is destroyed
    // otherwise component's reference in callback becomes broken
    window.removeEventListener('keydown', this.keydown)
    EventBus.$off('get-workflow')
    EventBus.$off('open-tab-event')
  },
  methods: {
    ...mapActions({
      debounceUpdateWorkflow: 'debounceUpdateWorkflow/debounceUpdateWorkflow'
    }),
    openTab (tab) {
      this.$router.push({ query: { selectedTab: tab } })
    },
    updateWorkflowStatus (status) {
      if (this.$store.state.workflow.isViewOnlyEnabled) {
        this.$store.commit('setWorkflowDisabled', true)
        return true
      }
      this.$store.commit('setWorkflowDisabled', status === 2)
      return status === 2
    },
    onAskForReply (event) {
      this.$refs.bpmn.onAskForReply(event)
    },
    createMainParentChildEventMap (events) {
      return dataHelpers.createMainParentChildEventMap(events)
    },
    keydown (event) {
      if (this.documentHasDialogOverlay()) {
        // stop propagation if overlay is present
        // for example when help dialog is open
        // don't allow keyboard shortcuts in the diagram
        // because the user might not see the result
        event.stopPropagation()
        return
      }
      if (['INPUT', 'TEXTAREA'].includes(event.target.tagName) || event.target.contentEditable === 'true') {
        return
      }
      if (this.workflowSettingsOpen || this.workflowStatusCompleted || this.$store.state.workflow.isViewOnlyEnabled) {
        return
      }
      if (event.key === 'z' && (event.ctrlKey || event.metaKey) && !event.shiftKey) {
        this.debounceUpdateWorkflow({
          fn: workflowApi.undo,
          args: [this.workflow]
        })
        event.preventDefault()
      }
      if ((event.key === 'y' && event.ctrlKey) || (event.key === 'y' && event.metaKey) || (event.key === 'z' && event.metaKey && event.shiftKey)) {
        this.debounceUpdateWorkflow({
          fn: workflowApi.redo,
          args: [this.workflow]
        })
        event.preventDefault()
      }
      if (!this.selectedEvent) {
        return
      }
      if (this.commentsDialog || this.dialog || this.exportDialog || this.exportJiraDialog) {
        return
      }
      if (event.keyCode === 13) {
        const eventId = this.$route.params.eventId
        const contenteditable = document.getElementById(eventId)
        setTimeout(() => {
          contenteditable.focus()
          inputHelpers.moveCursorToTextEnd(contenteditable)
        }, 0)
        return
      }
      if ((event.keyCode === 37 && event.altKey === false) || (event.keyCode === 9 && event.shiftKey === true)) { // left arrow (select previous event)
        this.moveLeft()
        event.preventDefault()
        return
      }
      if (event.keyCode === 38 && event.altKey === false) { // up arrow (select event above)
        const nodes = Object.values(this.svgGraphData).filter(node => node.x === this.svgGraphData[this.selectedEvent.id].x && node.y < this.svgGraphData[this.selectedEvent.id].y)
        nodes.sort(function (a, b) {
          return b.y - a.y
        })
        if (nodes[0]) {
          this.selectEvent(nodes[0].id)
          event.preventDefault()
        }
        return
      }
      if ((event.keyCode === 39 && event.altKey === false) || (event.keyCode === 9 && event.shiftKey === false)) { // right arrow (select next event)
        this.moveRight()
        event.preventDefault()
        return
      }
      if (event.keyCode === 40 && event.altKey === false) { // down arrow (select event below)
        const nodes = Object.values(this.svgGraphData).filter(node => (node.x === this.svgGraphData[this.selectedEvent.id].x) && (node.y > this.svgGraphData[this.selectedEvent.id].y))
        nodes.sort(function (a, b) {
          return a.y - b.y
        })
        if (nodes[0]) {
          this.selectEvent(nodes[0].id)
          event.preventDefault()
        }
        return
      }
      if (event.keyCode === 38 && event.altKey === true) { // option - up arrow (create event on lane above)
        if (this.workflowStatusCompleted) {
          return
        }

        const laneId = this.$refs.bpmn.svgLanes[this.selectedEvent.laneId].laneAbove
        if (laneId) {
          this.debounceUpdateWorkflow({
            fn: this.$refs.bpmn.addShape,
            args: [{ laneId }]
          })
        }
        return
      }
      if (event.keyCode === 39 && event.altKey === true) { // option - right arrow (create event on same lane)
        if (this.workflowStatusCompleted) {
          return
        }
        this.debounceUpdateWorkflow({ fn: this.$refs.bpmn.addShape, args: [] })
        return
      }
      if (event.keyCode === 40 && event.altKey === true) { // option - down arrow (create event on  lane below)
        if (this.workflowStatusCompleted) {
          return
        }
        event.preventDefault()
        const shape = { laneId: this.$refs.bpmn.svgLanes[this.selectedEvent.laneId].laneBelow }
        const options = { createNewLaneIfNoLaneId: true }
        this.debounceUpdateWorkflow({
          fn: this.$refs.bpmn.addShape,
          args: [shape, options]
        })
        return
      }
      if (event.key === 'Backspace' && event.altKey === true) {
        if (this.workflowStatusCompleted) {
          return
        }
        if (this.selectedEvent) {
          this.$refs.deleteItemDialog.openDialog()
        }
      }
      if (
        // when user starts to type on a selected
        // event or decision without cursor
        (event.keyCode === 8 ||
          (event.keyCode >= 48 &&
            event.keyCode <= 90) ||
          (event.keyCode >= 160 &&
            event.keyCode <= 173)) &&
        event.ctrlKey === false &&
        event.metaKey === false &&
        event.altKey === false &&
        // This is to prevent typing in the event when text editor (e.g. in aggregates) is focused
        event.target.classList.contains('ql-editor') === false
      ) {
        const eventId = this.$route.params.eventId
        const contenteditable = document.getElementById(eventId)
        contenteditable.innerHTML = ''
        contenteditable.focus()
      }
    },
    moveLeft () {
      if (this.selectedEvent.parents[0] && this.selectedEvent.parents[0] !== 'start') {
        this.selectEvent(this.selectedEvent.parents[0])
      }
    },
    moveRight () {
      const nodes = Object.values(this.svgGraphData).filter(node => node.parents[0] === this.selectedEvent.id)
      const currentY = this.svgGraphData[this.selectedEvent.id].y
      nodes.sort((a, b) => {
        return Math.abs(currentY - a.y) - Math.abs(currentY - b.y)
      })
      if (nodes[0]) {
        this.selectEvent(nodes[0].id)
      }
    },
    showAll () {
      this.eventId = null
      this.$router.push({ path: '/workflow/' + this.$route.params.projectId + '/' + this.$route.params.id }).catch(() => {
      })
    },
    selectEvent (eventId) {
      this.$router.push({ path: '/workflow/' + this.$route.params.projectId + '/' + this.$route.params.id + '/' + eventId }).catch(() => {
      })
      scrollToEvent(eventId)
    },
    onShowDoneChange (val) {
      this.showDone = val
    },
    checkForVersionGap () {
      if (this.workflow) {
        const restoreVersion = this.workflow.sameAsVersion ? (this.workflow.sameAsVersion - 1) : this.workflow.version - 1
        const index = this.versions?.findIndex(element => element.version === restoreVersion)
        if (index === -1) {
          this.$store.dispatch('workflowHistory/listWorkflowHistory', {
            projectId: this.workflow.projectId,
            workflowId: this.workflow.id,
            subscribe: false
          })
        }
      }
      this.checkVersionTimeout = setTimeout(this.checkForVersionGap, 5000)
    },

    // copy active event to clipboard
    onCopy (event) {
      if (document.activeElement !== document.body) {
        // don't catch these events because they can be input fields
        return
      }
      if (this.documentHasDialogOverlay()) {
        return // if the overlay is present, don't copy with this function, run default copy
      }
      if (this.selectedEvent) {
        // copy the json data to the clipboard's json format
        const jsonData = JSON.stringify({
          ...this.selectedEvent,
          workflowId: this.workflow.id,
          requirementTypes: this.requirementTypes,
          color: this.selectedEvent.color, // ? this.selectedEvent.color : this.workflow.eventDefinition.color,
          laneName: this.svgLanesData[this.selectedEvent.laneId].name
        })
        event.clipboardData.setData('application/json', jsonData)
        // if we have an active cursor on the event, then
        // do a standard copy of the selection
        // but if the whole event is selected (no cursor), then
        // copy the whole description to the clipboard
        if (document.activeElement.id !== this.selectedEvent.id) {
          // special copy, full description
          event.clipboardData.setData('text/plain', this.selectedEvent?.description)
          event.preventDefault()
        } else {
          // dont't prevent default, let the browser do the copy
          // of the selected text, the json object is already copied
        }
        this.$store.commit('snackbar/showMessage', { content: 'Copied', color: 'white' }, { root: true })
      }
    },

    async onPaste (event) {
      if (document.activeElement !== document.body) {
        // don't catch these events because they can be input fields
        return
      }
      if (this.workflowStatusCompleted) {
        return
      }
      if (!this.$refs.bpmn) {
        return
      }
      // only paste if we don't have a cursor on the event
      // if we have a cursor, then let the browser handle the paste
      if (!this.selectedEvent?.id || (this.selectedEvent.id !== document.activeElement.id)) {
        try {
          const clipboardData = event.clipboardData.getData('application/json')
          if (clipboardData) {
            const jsonData = JSON.parse(clipboardData)
            if (copyPasteHelpers.isValidShape(jsonData)) {
              const shape = copyPasteHelpers.prepareAddShape(jsonData, this.selectedEvent, this.svgGraphData, this.svgLanesData, this.workflow, this.requirementTypes)
              await this.$refs.bpmn.addShape(shape)
              event.preventDefault()
            } else {
              this.$store.commit('snackbar/showMessage', { content: 'Invalid element', color: 'red' }, { root: true })
            }
          }
        } catch (error) {
          // Handle JSON parse errors or any other errors
          console.error('Error parsing clipboard data:', error)
          this.$store.commit('snackbar/showMessage', {
            content: 'Error pasting data. Please try again.',
            color: 'red'
          }, { root: true })
        }
      }
    },

    async onCut (event) {
      if (document.activeElement !== document.body) {
        // don't catch these events because they can be input fields
        return
      }
      if (this.selectedEvent?.id && (this.selectedEvent.id !== document.activeElement.id) && this.$refs.deleteItemDialog) {
        if (this.svgGraphData[this.selectedEvent.id].childIds?.length > 1) {
          this.$refs.deleteItemDialog.openDialog()
        } else {
          this.onCopy(event)
          await this.$refs.deleteItemDialog.deleteItem()
        }
      }
    },
    documentHasDialogOverlay () {
      return document.querySelector('.v-overlay__scrim') !== null
    },
    async saveThumbnail () {
      const svgElement = document.getElementById('svg')?.cloneNode(true)

      if (!svgElement) {
        return
      }

      // Example criteria: Remove elements with a specific class
      // Adjust the selector to match your criteria (e.g., tag name, class, attribute)
      let elementsToRemove = svgElement.querySelectorAll('.swime-lane-path')

      // Iterate over the NodeList and remove each element from the SVG
      elementsToRemove.forEach(element => {
        element.parentNode.removeChild(element)
      })

      elementsToRemove = svgElement.querySelectorAll('.main-rect')
      elementsToRemove.forEach(element => {
        element.parentNode.removeChild(element)
      })

      elementsToRemove = svgElement.querySelectorAll('.add-event-button')
      elementsToRemove.forEach(element => {
        element.parentNode.removeChild(element)
      })

      // Remove rect objects that row is greater than 3 or column is greater 9
      elementsToRemove = svgElement.querySelectorAll('rect')
      elementsToRemove.forEach(element => {
        const xValue = parseFloat(element.getAttribute('x'))
        const yValue = parseFloat(element.getAttribute('y'))
        if (xValue > 1360 || yValue > 423) {
          if (element.parentNode.classList.contains('event-group')) { // event rect
            const toBeRemoved = element.parentNode.parentNode
            toBeRemoved.parentNode.removeChild(toBeRemoved)
          } else if (element.parentNode.parentNode && element.parentNode.parentNode.id === 'svg') { // rect which is connected to the lane
            element.parentNode.parentNode.removeChild(element.parentNode)
          } else { // child rect of svg
            element.parentNode.removeChild(element)
          }
        }
      })

      // Remote foreignObjects that will be invisible in thumbnail
      elementsToRemove = svgElement.querySelectorAll('foreignObject')
      elementsToRemove.forEach(element => {
        const xValue = parseFloat(element.getAttribute('x'))
        const yValue = parseFloat(element.getAttribute('y'))
        if (xValue > 1365 || yValue > 423) {
          element.parentNode.removeChild(element)
        }
      })

      // Remove path and circle that its event row is greater than 3 or column is greater 9
      elementsToRemove = svgElement.querySelectorAll('circle')
      elementsToRemove.forEach(element => {
        const xValue = parseFloat(element.getAttribute('cx'))
        const yValue = parseFloat(element.getAttribute('cy'))
        if (xValue > 1465 || yValue > 390) {
          element.parentNode.removeChild(element.nextSibling) // path
          element.parentNode.removeChild(element) // circle
        }
      })

      elementsToRemove = svgElement.querySelectorAll('mask')
      elementsToRemove.forEach(element => {
        element.parentNode.removeChild(element)
      })

      elementsToRemove = svgElement.querySelectorAll('defs')
      elementsToRemove.forEach(element => {
        element.parentNode.removeChild(element)
      })

      svgElement.setAttribute('viewBox', '20 0 3000 450')
      svgElement.setAttribute('height', '146px')
      svgElement.setAttribute('width', '974px')

      const serializer = new XMLSerializer()
      const commentRegex = /<!--[\s\S]*?-->/g
      const svgString = serializer.serializeToString(svgElement).replace(commentRegex, '')

      const data = await store.dispatch('workflow/updateWorkflowNoHistory', {
        projectId: this.workflow.projectId,
        input: {
          id: this.workflow.id,
          thumbnail: svgString,
          expectedVersion: this.workflow.version
        }
      })

      if (data?.data?.updateWorkflowNoHistory) {
        await store.commit('workflows/updateWorkflows', data?.data?.updateWorkflowNoHistory)
      }
    },

    async updateWorkflowQueries (workflowId) {
      if (workflowId) {
        this.$store.commit('workflow/setLoading', true)
        await this.$store.dispatch('workflow/getWorkflow', this.$route.params)
        // run listWorkflowHistory after getWorkflow
        // because we need the current workflow to be set
        // when setting the workflow history in the store
        // also run listRequirementTypes after getWorkflow
        // so we can avoid subscriptions on public workflows
        const requirementTypesPromise = this.$store.dispatch('requirementTypes/listRequirementTypes', {
          workflowId
        })
        const listWorkflowHistoryPromise = this.$store.dispatch('workflowHistory/listWorkflowHistory', {
          projectId: this.$route.params.projectId,
          workflowId
        })
        await Promise.all([requirementTypesPromise, listWorkflowHistoryPromise])
        this.$store.commit('workflow/setLoading', false)
      } else {
        this.$store.commit('workflow/setWorkflow', {})
      }
    }
  },
  beforeRouteLeave (to, from, next) {
    if (to.path.includes('/workflow/')) {
      // we are not leaving the workflow, so no need to save the thumbnail
      next()
    } else {
      this.$store.commit('setHasClosedSidePanel', false)
      if (this.$store.state.workflow.isViewOnlyEnabled) {
        // view only mode, cannot save thumbnail
        next()
        return
      }
      this.saveThumbnail().then(() => {
        // save the thumbnail before leaving workflow
        // so that the thumbnail is up to date
        // if we load it again on the next page
        next()
      }).catch((error) => {
        console.error(error)
        next() // if saving the thumbnail fails, we still want to leave the page
      })
    }
  },
  async mounted () {
    EventBus.$on('get-workflow', (workflowId) => {
      this.updateWorkflowQueries(workflowId)
    })

    EventBus.$on('open-tab-event', (params) => {
      const { tabId, eventId } = params

      EventBus.$emit('enable-connection-targets', false)
      this.$router.push({ path: '/workflow/' + this.$route.params.projectId + '/' + this.$route.params.id + '/' + eventId, query: { selectedTab: tabId } })
    })

    if (!window.Cypress) {
      this.checkVersionTimeout = setTimeout(this.checkForVersionGap, 5000)
    }
  },
  unmounted () {
    clearTimeout(this.checkVersionTimeout)
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.workflow-heading {
  cursor: pointer;
}

.setting-search-field {
  padding-top: 6px;
}

.v-list__tile__sub-title {
  font-size: 10px;
}

.theme--light.v-subheader {
  text-decoration: underline;
  color: black;
}

.container {
  padding-top: 0px;
}

#overlay {
  position: fixed; /* Sit on top of the page content */
  display: none; /* Hidden by default */
  width: 100%; /* Full width (cover the whole page) */
  height: 100%; /* Full height (cover the whole page) */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5); /* Black background with opacity */
  z-index: 2; /* Specify a stack order in case you're using a different order for other elements */
  cursor: pointer; /* Add a pointer on hover */
}

.hidden {
  display: none;
}

.hover-underline:hover {
  text-decoration: underline;
}

.approval-button {
  margin-right: 0px;
}

.v-list__tile_comments > div {
  height: auto;
}

.workflow-settings-button {
  min-width: auto !important;
}

.v-tooltip__content {
  background: white;
  color: var(--color-primary);
  border-radius: 10px;
  max-width: 320px;
}

.bottom-container {
  margin: 0 -40px;
  padding: 28px 40px;
  border-radius: 24px 24px 0px 0px;
  background: #F5F2F0;
  height: 100%;
}

nowrap {
  white-space: nowrap;
}
</style>

<style>
.tab-button-container {
  width: fit-content;
  height: 38px;
  position: sticky;
  left: 0;
  max-width: calc(100% - 300px);
  overflow: hidden;
  z-index: 1;

  .v-slide-group__content {
    column-gap: 4px;
  }

  .v-tab.v-tab.v-btn {
    min-width: 70px;
  }
}

.tab-button:first-child {
  margin-left: 10px;
}

.tab-button {
  padding: 0 10px;
  max-width: 400px;
  box-sizing: border-box;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.tab-button.active {
  font-weight: 500;
  border-bottom: 2px solid #022a44;
}

.tab-button.inactive {
  color: #9e9e9e;
  font-weight: 400;
}

.tab-button:focus {
  outline: 0;
}

</style>
